import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown, Check, Building2 } from 'lucide-react';
import InternalAffaireBadge from './InternalAffaireBadge.jsx';

/**
 * Sprint 20 — Picker d'affaire repensé (combobox à panneau).
 *
 * Remplace l'ancien couple « input filtre + <select> natif déconnecté » par un
 * combobox unique :
 *  - un déclencheur qui affiche clairement l'affaire choisie (pastille couleur
 *    + code + nom + client) ou le placeholder ;
 *  - au clic, un panneau s'ouvre avec une recherche en haut et la liste des
 *    résultats **groupés par client** juste dessous ;
 *  - cliquer un résultat le sélectionne immédiatement et referme le panneau
 *    (l'action est visible et directe) ;
 *  - le texte recherché est surligné dans les libellés (nom / code / client) ;
 *  - navigation clavier ↑/↓/Entrée/Échap, fermeture au clic extérieur.
 *
 * Filtre case-insensitive sur `name`, `code` et `client.name` (libellé complet
 * ou partiel, ou nom de client).
 *
 * Props (rétro-compatibles avec l'ancien composant) :
 *  - affaires       : Array<{ id, name, code?, color?, client?: { name? } }>
 *  - value          : id sélectionné (string ou '')
 *  - onChange       : (id) => void
 *  - id             : id HTML du déclencheur (pour <label htmlFor>)
 *  - disabled       : booléen
 *  - placeholder    : libellé quand rien n'est sélectionné
 *  - emptyMessage   : libellé si le filtre ne ramène rien
 *  - autoFocus      : ouvre + focus la recherche au montage
 *  - selectStyle    : style additionnel pour le déclencheur (compat ancien)
 *  - inputStyle     : style additionnel pour l'input recherche
 *  - inline         : panneau rendu dans le flux (et non en dropdown ancré),
 *                     occupant toute la hauteur disponible du parent flex.
 *                     Destiné aux modales dont le picker EST le contenu :
 *                     la liste porte alors le seul scroll (ticket #51).
 */
export default function AffaireFilterableSelect({
  affaires,
  value,
  onChange,
  id,
  disabled = false,
  placeholder = '— Sélectionner une affaire —',
  emptyMessage = 'Aucune affaire ne correspond à la recherche',
  autoFocus = false,
  selectStyle = {},
  inputStyle = {},
  inline = false,
}) {
  // En mode inline, le panneau n'est jamais replié : il constitue le corps de
  // la modale hôte, et le refermer ferait s'effondrer sa hauteur.
  const [openState, setOpen] = useState(false);
  const open = inline || openState;
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();

  const selected = useMemo(
    () => affaires.find((a) => a.id === value) || null,
    [affaires, value],
  );

  // Filtre + tri + regroupement par client.
  const { groups, flatOptions, total } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? affaires.filter((a) =>
          [a.name ?? '', a.code ?? '', a.client?.name ?? '']
            .join(' ')
            .toLowerCase()
            .includes(q),
        )
      : affaires;

    const byClient = new Map();
    for (const a of matched) {
      const key = a.client?.name?.trim() || '— Sans client —';
      if (!byClient.has(key)) byClient.set(key, []);
      byClient.get(key).push(a);
    }

    const sortedGroups = [...byClient.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
      .map(([clientName, items]) => ({
        clientName,
        items: items.sort((x, y) =>
          (x.name ?? '').localeCompare(y.name ?? '', 'fr'),
        ),
      }));

    // Liste plate (pour la navigation clavier), dans l'ordre d'affichage.
    const flat = [];
    for (const g of sortedGroups) for (const it of g.items) flat.push(it);

    return { groups: sortedGroups, flatOptions: flat, total: matched.length };
  }, [affaires, query]);

  // Ouverture auto au montage si demandé.
  useEffect(() => {
    if (autoFocus) setOpen(true);
  }, [autoFocus]);

  // Focus de la recherche + reset du highlight à l'ouverture.
  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open || inline) return;
    function onDocPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [open, inline]);

  // Garde l'option surlignée visible dans le scroll.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector('[data-highlighted="true"]');
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  function commit(affaire) {
    if (!affaire) return;
    onChange(affaire.id);
    if (inline) return; // le panneau reste ouvert : la sélection se voit à la coche
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, flatOptions.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(Math.max(0, flatOptions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(flatOptions[highlight]);
    } else if (e.key === 'Escape') {
      // En inline, on laisse remonter : c'est la modale hôte qui se ferme.
      if (inline) return;
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  }

  const noResults = query.trim().length > 0 && total === 0;

  return (
    <div
      ref={rootRef}
      style={
        inline
          ? {
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              flex: 1,
              gap: 8,
            }
          : { position: 'relative' }
      }
    >
      {/* Déclencheur : montre la sélection courante. */}
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && !inline && setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          ...selectStyle,
        }}
      >
        {selected ? (
          <>
            <span
              aria-hidden
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                flexShrink: 0,
                background: selected.color || 'var(--accent)',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
              }}
            />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'var(--text)',
              }}
            >
              {selected.code ? (
                <span style={{ color: 'var(--text-muted)' }}>
                  [{selected.code}]{' '}
                </span>
              ) : null}
              {selected.name}
              {selected.isInternal ? (
                <span style={{ marginLeft: 6 }}>
                  <InternalAffaireBadge size="sm" expiresAt={selected.expiresAt} />
                </span>
              ) : null}
              {selected.client?.name ? (
                <span style={{ color: 'var(--text-muted)' }}>
                  {' '}
                  — {selected.client.name}
                </span>
              ) : null}
            </span>
          </>
        ) : (
          <span style={{ flex: 1, color: 'var(--text-muted)' }}>
            {placeholder}
          </span>
        )}
        {selected && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            title="Désélectionner"
            aria-label="Désélectionner"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--text-muted)',
              flexShrink: 0,
            }}
          >
            <X size={14} />
          </span>
        )}
        {inline ? null : (
          <ChevronDown
            size={16}
            style={{
              color: 'var(--text-muted)',
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          />
        )}
      </button>

      {/* Panneau de recherche + résultats. */}
      {open && (
        <div
          style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-card)',
            overflow: 'hidden',
            ...(inline
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                }
              : {
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  zIndex: 1200,
                }),
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderBottom: '1px solid var(--glass-border)',
              background: 'var(--glass)',
            }}
          >
            <Search
              size={14}
              style={{ color: 'var(--text-muted)', flexShrink: 0 }}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder="Filtrer par client, nom ou code…"
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-autocomplete="list"
              style={{
                flex: 1,
                background: 'transparent',
                color: 'var(--text)',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                ...inputStyle,
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                title="Effacer le filtre"
                aria-label="Effacer le filtre"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'inline-flex',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 4,
              overflowY: 'auto',
              ...(inline
                ? { flex: 1, minHeight: 0 }
                : { maxHeight: 280 }),
            }}
          >
            {noResults && (
              <li
                style={{
                  padding: '10px 12px',
                  color: 'var(--text-muted)',
                  fontSize: 12,
                }}
              >
                {emptyMessage}
              </li>
            )}
            {!noResults && total === 0 && (
              <li
                style={{
                  padding: '10px 12px',
                  color: 'var(--text-muted)',
                  fontSize: 12,
                }}
              >
                Aucune affaire disponible
              </li>
            )}
            {groups.map((group) => (
              <li key={group.clientName} role="presentation">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 8px 2px',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    color: 'var(--text-muted)',
                  }}
                >
                  <Building2 size={12} style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {highlightMatch(group.clientName, query)}
                  </span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {group.items.map((a) => {
                    const idx = flatOptions.indexOf(a);
                    const isHighlighted = idx === highlight;
                    const isSelected = a.id === value;
                    return (
                      <li
                        key={a.id}
                        role="option"
                        aria-selected={isSelected}
                        data-highlighted={isHighlighted ? 'true' : 'false'}
                        onMouseEnter={() => setHighlight(idx)}
                        onPointerDown={(e) => {
                          e.preventDefault();
                          commit(a);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 8px',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          background: isHighlighted
                            ? 'var(--glass)'
                            : 'transparent',
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            flexShrink: 0,
                            background: a.color || 'var(--accent)',
                            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: 'var(--text)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {highlightMatch(a.name ?? '', query)}
                          </div>
                          {a.code && (
                            <div
                              style={{
                                fontSize: 11,
                                color: 'var(--text-muted)',
                              }}
                            >
                              {highlightMatch(a.code, query)}
                            </div>
                          )}
                        </div>
                        {a.isInternal ? (
                          <InternalAffaireBadge size="sm" expiresAt={a.expiresAt} />
                        ) : null}
                        {isSelected && (
                          <Check
                            size={15}
                            style={{ color: 'var(--accent)', flexShrink: 0 }}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>

          {total > 0 && (
            <div
              style={{
                padding: '4px 10px',
                borderTop: '1px solid var(--glass-border)',
                fontSize: 11,
                color: 'var(--text-muted)',
              }}
            >
              {query
                ? `${total} sur ${affaires.length} affaire${affaires.length > 1 ? 's' : ''}`
                : `${affaires.length} affaire${affaires.length > 1 ? 's' : ''}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Surligne (gras + couleur accent) la portion de `text` qui matche `query`.
 * Retourne une string si pas de match (évite des spans inutiles).
 */
function highlightMatch(text, query) {
  const q = query.trim();
  if (!q) return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <mark
        style={{
          background: 'transparent',
          color: 'var(--accent)',
          fontWeight: 700,
        }}
      >
        {match}
      </mark>
      {after}
    </>
  );
}
